/**
 * Data Helper Functions for BYOR
 * Extracted from ReportBuilder.jsx
 */

/**
 * Get data by path with intelligent key matching (matches backend logic)
 * Handles: exact matches, case-insensitive, category name mapping, fuzzy matching
 * Also supports 'internal.' and 'external.' prefixes for combined scan reports
 */
export const getDataByPath = (obj, path) => {
    if (!path || !obj) return null;

    // Parse the path to handle both dot notation and bracket notation
    // e.g., "network_devices[21].system.Administrators" -> ["network_devices", "21", "system", "Administrators"]
    const parsePath = (pathStr) => {
        const parts = [];
        let current = '';
        let inBracket = false;

        for (let i = 0; i < pathStr.length; i++) {
            const char = pathStr[i];

            if (char === '[') {
                if (current) {
                    parts.push(current);
                    current = '';
                }
                inBracket = true;
            } else if (char === ']') {
                if (current) {
                    parts.push(current);
                    current = '';
                }
                inBracket = false;
            } else if (char === '.' && !inBracket) {
                if (current) {
                    parts.push(current);
                    current = '';
                }
            } else {
                current += char;
            }
        }

        if (current) {
            parts.push(current);
        }

        return parts;
    };

    const keys = parsePath(path);
    let current = obj;

    // Mapping of humanized category names to actual scan data keys (same as backend)
    // Also includes 'internal' and 'external' for combined scan data access
    const categoryMap = {
        'Active Directory Scan': 'Ad Scan',
        'Active Directory': 'Ad Scan',
        'Microsoft 365 Scan': 'MS365',
        'Microsoft 365': 'MS365',
        'Network Scan': 'network_devices',
        'Network Devices': 'network_devices',
        'On-Prem Scan': 'Onprem',
        'On Prem': 'Onprem',
        'Onprem': 'Onprem',
        'Localhost Scan': 'localhost',
        'Localhost': 'localhost',
        'Personally Identifiable Information': 'PII',
        // Combined scan data namespaces
        'Internal Scan': 'internal',
        'Internal': 'internal',
        'External Scan': 'external',
        'External': 'external',
        'Domain Scan': 'external'
    };

    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];

        if (current === null || current === undefined) {
            return null;
        }

        // Handle array index access (numeric key)
        if (/^\d+$/.test(key)) {
            if (Array.isArray(current)) {
                const index = parseInt(key, 10);
                if (index >= 0 && index < current.length) {
                    current = current[index];
                    continue;
                }
            }
            // Also try as object key
            if (typeof current === 'object' && key in current) {
                current = current[key];
                continue;
            }
            return null;
        }

        if (typeof current !== 'object') {
            return null;
        }

        // Try exact match first
        if (key in current) {
            current = current[key];
            continue;
        }

        // Try category mapping for first key (usually a category name)
        if (i === 0 && categoryMap[key]) {
            const mappedKey = categoryMap[key];
            if (mappedKey in current) {
                current = current[mappedKey];
                continue;
            }
        }

        // Try case-insensitive match
        const foundKey = Object.keys(current).find(k =>
            k.toLowerCase() === key.toLowerCase()
        );
        if (foundKey) {
            current = current[foundKey];
            continue;
        }

        // Try fuzzy match (key contains or is contained by)
        const fuzzyKey = Object.keys(current).find(k =>
            k.toLowerCase().includes(key.toLowerCase()) ||
            key.toLowerCase().includes(k.toLowerCase())
        );
        if (fuzzyKey) {
            current = current[fuzzyKey];
            continue;
        }

        // Try removing spaces and matching
        const normalizedKey = key.replace(/\s+/g, '');
        const normalizedMatch = Object.keys(current).find(k =>
            k.replace(/\s+/g, '').toLowerCase() === normalizedKey.toLowerCase()
        );
        if (normalizedMatch) {
            current = current[normalizedMatch];
            continue;
        }

        // No match found
        return null;
    }

    return current;
};
